import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  LifeBuoy, Truck, Boxes, Zap,
  RefreshCw, ChevronDown, ChevronRight, ChevronUp, X
} from 'lucide-react';

import { getRadarDashboard } from '../../services/api';

const INTERVALO_REFRESH_MS = 2 * 60 * 1000;

const RADAR_META = {
  posvenda: { icone: LifeBuoy, cor: 'violet' },
  vendas: { icone: Truck, cor: 'blue' },
  abastecimento: { icone: Boxes, cor: 'emerald' },
  automacao: { icone: Zap, cor: 'orange' },
};

const ACENTO = {
  violet: 'bg-violet-50 text-violet-600 ring-violet-100',
  blue: 'bg-blue-50 text-blue-600 ring-blue-100',
  emerald: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
  orange: 'bg-orange-50 text-orange-600 ring-orange-100',
};

const NIVEL = {
  ok: { ponto: 'bg-emerald-500', halo: 'ring-emerald-100', valor: 'text-slate-800' },
  atencao: { ponto: 'bg-amber-500', halo: 'ring-amber-100', valor: 'text-amber-600' },
  critico: { ponto: 'bg-red-500', halo: 'ring-red-100', valor: 'text-red-600' },
  rodando: { ponto: 'bg-sky-500', halo: 'ring-sky-100', valor: 'text-sky-600' },
  neutro: { ponto: 'bg-slate-300', halo: 'ring-slate-100', valor: 'text-slate-400' },
};

const ROTAS = {
  vendas_hoje: '/vendas',
  orcamentos: '/orcamentos',
  nfe: '/notas_fiscais',
  promocoes: '/promocoes',
  denuncias: '/anuncios/denuncias',
  estoque: '/gestao_estoque',
  consultas_estoque: '/produtos-bloqueados?aba=consultas',
  reclamacoes: '/reclamacoes',
  devolucoes: '/devolucoes',
  mensagens: '/mensageria',
  perguntas: '/perguntas',
  pendencias: '/pendencias-vendas',
  despacho_conferir: '/despacho',
  retirada: '/despacho',
  furos: '/cobertura-furos',
  listas_fornecedores: '/logs_produtos',
  listas_compras: '/listas-compras/kanban',
  cotacoes: '/cotacoes',
  imagens: '/logs-imagens',
  saldo_me: '/melhor-envio',
  lalamove: '/lalamove',
  anuncios: '/produtos',
  bot_poli: '/bot-atendimento',
  retira_atraso: '/bot-atendimento?aba=atrasos',
  cotacoes_email: '/cotacoes-email',
  pos_venda: '/bot-atendimento?aba=posvenda',
  custos_ia: '/bot-custos-ia',
  openrouter: '/dimensoes',
  erros_ml: '/logs',
};

const horaCurta = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return null;
  }
};

/** Hoje mostra só a hora; outro dia mostra data + hora, para não confundir. */
const quandoLegivel = (iso) => {
  if (!iso) return 'nunca rodou';
  try {
    const d = new Date(iso);
    const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (d.toDateString() === new Date().toDateString()) return hora;
    return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${hora}`;
  } catch {
    return '';
  }
};

/**
 * Tooltip da linha: explica o que o número significa e abre o detalhamento.
 * Some no mobile (sem hover) - lá o card já mostra o essencial.
 */
const ItemDetalhe = ({ item }) => {
  const nivel = NIVEL[item.nivel] || NIVEL.neutro;
  const temValor = item.valor !== undefined && item.valor !== null;

  return (
    <div className="flex items-baseline gap-1.5 py-[3px]">
      <span className={`mt-1 h-1.5 w-1.5 shrink-0 self-start rounded-full ${nivel.ponto}`} />
      <div className="min-w-0 flex-1">
        <span className="block text-[10.5px] font-semibold leading-tight text-slate-700">{item.nome}</span>
        {item.estado && (
          <span className="block text-[9.5px] leading-tight text-slate-400">{item.estado}</span>
        )}
      </div>
      <span className={`shrink-0 text-[11px] font-bold leading-tight tabular-nums ${nivel.valor}`}>
        {temValor ? item.valor : quandoLegivel(item.quando)}
      </span>
    </div>
  );
};

/** Acima disso a lista vira duas colunas: cresce na horizontal, não na vertical. */
const ITENS_ATE_UMA_COLUNA = 5;
const DURACAO_LONG_PRESS = 450;

const ListaDetalhes = ({ itens }) => (
  <div className={itens.length > ITENS_ATE_UMA_COLUNA ? 'grid grid-cols-2 gap-x-4' : ''}>
    {itens.map((item) => <ItemDetalhe key={item.nome} item={item} />)}
  </div>
);

/**
 * De que lado o tooltip abre, por posição do card na grade. Nas colunas da
 * direita ele precisa abrir para a esquerda, senão estoura a tela: no tablet
 * (2 colunas) isso vale para os cards 1 e 3; no desktop (4 colunas), só o 3.
 */
const ALINHAMENTO_TOOLTIP = [
  'left-0',
  'right-0 xl:right-auto xl:left-0',
  'left-0',
  'right-0',
];

const detalhesDaLinha = (linha) => {
  const itens = Array.isArray(linha.itens) ? linha.itens : [];
  const grupos = (Array.isArray(linha.grupos) ? linha.grupos : [])
    .filter((g) => Array.isArray(g.itens) && g.itens.length > 0);
  return { itens, grupos };
};

const ConteudoTooltip = ({ linha }) => {
  const { itens, grupos } = detalhesDaLinha(linha);

  return (
    <>
      <p className="mb-1 text-[11px] font-bold leading-tight text-slate-800">{linha.label}</p>

      {linha.ajuda && (
        <p className="text-[10.5px] font-normal leading-relaxed text-slate-500">{linha.ajuda}</p>
      )}

      {itens.length > 0 && (
        <div className="mt-2 border-t border-slate-100 pt-2">
          <ListaDetalhes itens={itens} />
        </div>
      )}

      {grupos.map((grupo) => (
        <div key={grupo.titulo} className="mt-2 border-t border-slate-100 pt-2">
          <p className="mb-1 text-[9px] font-bold uppercase tracking-wider text-slate-400">{grupo.titulo}</p>
          <ListaDetalhes itens={grupo.itens} />
        </div>
      ))}
    </>
  );
};

const TooltipLinha = ({ linha, alinharDireita }) => {
  const { itens, grupos } = detalhesDaLinha(linha);

  // Só alarga quando há mesmo o que distribuir em duas colunas.
  const largo = itens.length > ITENS_ATE_UMA_COLUNA
    || grupos.some((g) => g.itens.length > ITENS_ATE_UMA_COLUNA);

  return (
    <div
      role="tooltip"
      className={`pointer-events-none absolute top-full z-40 mt-1.5 hidden origin-top rounded-xl border border-slate-200/70 bg-white p-3 text-left ring-1 ring-slate-900/5 shadow-[0_2px_4px_-1px_rgba(15,23,42,0.06),0_12px_24px_-6px_rgba(15,23,42,0.20),0_32px_64px_-16px_rgba(15,23,42,0.40)] sm:group-hover/linha:block ${largo ? 'w-[400px]' : 'w-[248px]'} ${alinharDireita}`}
    >
      <ConteudoTooltip linha={linha} />
    </div>
  );
};

/**
 * No mobile o tooltip NÃO pode ser absoluto dentro do card: o carrossel dos
 * radares usa overflow-x-auto para o swipe, e todo container com overflow
 * recorta o que é absoluto lá dentro. Por isso vai num portal para o body,
 * como folha ancorada embaixo, fora do alcance de qualquer recorte.
 */
const FolhaMobile = ({ linha, aberto, onFechar }) => {
  useEffect(() => {
    if (!aberto) return undefined;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = anterior; };
  }, [aberto]);

  if (!aberto) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end sm:hidden" role="dialog" aria-modal="true">
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={onFechar}
        className="absolute inset-0 h-full w-full cursor-default bg-slate-900/50 backdrop-blur-[2px]"
      />
      <div className="relative mx-2 mb-2 max-h-[80vh] w-full overflow-y-auto overscroll-contain rounded-2xl border border-slate-200 bg-white p-4 pt-3 shadow-[0_-8px_40px_-8px_rgba(15,23,42,0.45)]">
        <div className="mb-2 flex items-center gap-2">
          <span className="h-1 w-9 shrink-0 rounded-full bg-slate-200" />
          <span className="flex-1" />
          <button
            type="button"
            aria-label="Fechar"
            onClick={onFechar}
            className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors active:bg-slate-100 active:text-slate-600"
          >
            <X size={16} />
          </button>
        </div>
        <ConteudoTooltip linha={linha} />
      </div>
    </div>,
    document.body,
  );
};

const Linha = ({ linha, onAbrir, alinharTooltip }) => {
  const nivel = NIVEL[linha.nivel] || NIVEL.neutro;
  const critico = linha.nivel === 'critico';

  // No mobile nao existe hover: o tooltip abre segurando o dedo na linha.
  const [tooltipMobile, setTooltipMobile] = useState(false);
  const timerRef = useRef(null);
  const pressionadoRef = useRef(false);

  const cancelarPressao = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const iniciarPressao = useCallback(() => {
    pressionadoRef.current = false;
    cancelarPressao();
    timerRef.current = setTimeout(() => {
      pressionadoRef.current = true;
      setTooltipMobile(true);
    }, DURACAO_LONG_PRESS);
  }, [cancelarPressao]);

  useEffect(() => cancelarPressao, [cancelarPressao]);

  // Depois de um long press o dedo levanta e dispara click: navegar aqui
  // tiraria o usuario da tela no exato momento em que ele abriu o tooltip.
  const aoClicar = useCallback(() => {
    if (pressionadoRef.current) {
      pressionadoRef.current = false;
      return;
    }
    onAbrir(linha.id);
  }, [onAbrir, linha.id]);

  return (
    <>
    <button
      type="button"
      onClick={aoClicar}
      onTouchStart={iniciarPressao}
      onTouchEnd={cancelarPressao}
      onTouchMove={cancelarPressao}
      onTouchCancel={cancelarPressao}
      onContextMenu={(e) => {
        if (timerRef.current || pressionadoRef.current) e.preventDefault();
      }}
      className="group/linha relative flex w-full select-none items-center gap-2 rounded-md px-1.5 py-[5px] text-left transition-colors duration-150 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60"
    >
      <span className={`relative flex h-1.5 w-1.5 shrink-0 rounded-full ring-2 ${nivel.ponto} ${nivel.halo}`}>
        {critico && (
          <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${nivel.ponto}`} />
        )}
      </span>

      <span className="min-w-0 flex-1 truncate text-[11px] font-medium leading-none text-slate-600 group-hover/linha:text-slate-900">
        {linha.label}
      </span>

      {linha.detalhe && (
        <span className="hidden max-w-[92px] truncate text-[9.5px] font-medium leading-none text-slate-400 xl:block">
          {linha.detalhe}
        </span>
      )}

      <span className={`shrink-0 text-[12.5px] font-bold leading-none tabular-nums ${nivel.valor}`}>
        {linha.valor}
      </span>

      <ChevronRight
        size={11}
        className="-ml-1 shrink-0 text-slate-300 opacity-0 transition-opacity group-hover/linha:opacity-100"
      />

      <TooltipLinha linha={linha} alinharDireita={alinharTooltip} />
    </button>

    <FolhaMobile linha={linha} aberto={tooltipMobile} onFechar={() => setTooltipMobile(false)} />
    </>
  );
};

const LinhaSkeleton = () => (
  <div className="flex items-center gap-2 px-1.5 py-[5px]">
    <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-slate-200" />
    <span className="h-2 flex-1 animate-pulse rounded bg-slate-100" />
    <span className="h-2.5 w-6 animate-pulse rounded bg-slate-100" />
  </div>
);

const CardRadar = ({ radar, carregando, onAbrir, indice }) => {
  const meta = RADAR_META[radar?.id] || RADAR_META.posvenda;
  const Icone = meta.icone;
  const criticos = (radar?.linhas || []).filter((l) => l.nivel === 'critico').length;

  return (
    <div className="flex min-w-[82%] snap-start flex-col rounded-xl bg-white p-2.5 ring-1 ring-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_6px_16px_-8px_rgba(15,23,42,0.12)] transition-shadow duration-200 hover:shadow-[0_1px_2px_rgba(15,23,42,0.05),0_10px_24px_-10px_rgba(15,23,42,0.18)] sm:min-w-0">
      <div className="mb-1.5 flex items-center gap-2 border-b border-slate-100 pb-1.5">
        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md ring-1 ${ACENTO[meta.cor]}`}>
          <Icone size={11} strokeWidth={2.5} />
        </span>
        <h3 className="min-w-0 flex-1 truncate text-[10px] font-bold uppercase tracking-wider text-slate-500">
          {carregando ? <span className="block h-2 w-20 animate-pulse rounded bg-slate-100" /> : radar?.titulo}
        </h3>
        {!carregando && criticos > 0 && (
          <span className="shrink-0 rounded-full bg-red-50 px-1.5 py-px text-[9px] font-bold leading-tight text-red-600 ring-1 ring-red-100">
            {criticos}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-px">
        {carregando
          ? [0, 1, 2, 3].map((i) => <LinhaSkeleton key={i} />)
          : (radar?.linhas || []).map((linha) => (
            <Linha
              key={linha.id}
              linha={linha}
              onAbrir={onAbrir}
              alinharTooltip={ALINHAMENTO_TOOLTIP[indice % ALINHAMENTO_TOOLTIP.length]}
            />
          ))}
      </div>
    </div>
  );
};

const RadarDashboard = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [erro, setErro] = useState(false);
  const [aberto, setAberto] = useState(true);

  /**
   * manual = clique no botão: mostra o skeleton de novo, deixando claro que os
   * números estão sendo refeitos. O refresh automático atualiza em silêncio.
   */
  const carregar = useCallback(async (manual = false) => {
    setAtualizando(true);
    if (manual) setCarregando(true);

    try {
      const res = await getRadarDashboard();
      if (res) {
        setDados(res);
        setErro(false);
      } else {
        setErro(true);
      }
    } catch {
      setErro(true);
    } finally {
      setCarregando(false);
      setAtualizando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
    const timer = setInterval(() => carregar(), INTERVALO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [carregar]);

  const totalCriticos = useMemo(
    () => (dados?.radares || []).reduce(
      (soma, r) => soma + (r.linhas || []).filter((l) => l.nivel === 'critico').length,
      0
    ),
    [dados]
  );

  const radares = dados?.radares || [null, null, null, null];
  const atualizadoEm = horaCurta(dados?.gerado_em);

  return (
    <section className="mb-4">
      <div className="mb-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setAberto((v) => !v)}
          className="group flex min-w-0 items-center gap-2 rounded-md py-0.5 pr-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60"
        >
          <span className="flex h-4 w-4 items-center justify-center rounded text-slate-400 transition-colors group-hover:bg-slate-100 group-hover:text-slate-600">
            {aberto ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
          </span>
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400 opacity-70" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-orange-500" />
          </span>
          <span className="text-[11px] font-black uppercase tracking-widest text-slate-500 group-hover:text-slate-700">
            Radar do dia
          </span>
        </button>

        {!carregando && totalCriticos > 0 && (
          <span className="shrink-0 rounded-full bg-red-50 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-red-600 ring-1 ring-red-100">
            {totalCriticos} {totalCriticos === 1 ? 'crítico' : 'críticos'}
          </span>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {erro && (
            <span className="text-[9.5px] font-semibold uppercase tracking-wide text-red-500">
              falha ao atualizar
            </span>
          )}
          {!erro && atualizadoEm && (
            <span className="hidden text-[9.5px] font-medium text-slate-400 sm:block">
              {atualizadoEm}
            </span>
          )}
          <button
            type="button"
            onClick={() => carregar(true)}
            aria-label="Atualizar radar"
            className="group flex h-6 w-6 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-400 shadow-sm transition-all hover:border-orange-300 hover:bg-orange-50 hover:text-orange-500 hover:shadow active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60"
          >
            <RefreshCw
              size={12}
              className={`transition-transform ${atualizando ? 'animate-spin' : 'group-hover:animate-spin'}`}
            />
          </button>
        </div>
      </div>

      {aberto && (
        <div className="-mx-1 flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 xl:grid-cols-4">
          {radares.map((radar, i) => (
            <CardRadar
              key={radar?.id || i}
              radar={radar}
              carregando={carregando}
              indice={i}
              onAbrir={(id) => ROTAS[id] && navigate(ROTAS[id])}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default RadarDashboard;
